<template>
  <div class="image-importer">
    <div class="image-importer__group">
      <div class="image-importer__heading">
        <h4>{{ sourceLabel }}</h4>
      </div>
      <div class="file-upload-container">
        <input
          id="image-importer-file-select"
          class="d-none"
          type="file"
          multiple
          :accept="uploadAccept || undefined"
          @change="handleSelectFileInput"
        >
        <div
          class="file-upload-btn mt-3"
          :class="{ _highlight: dropHighlighted }"
          @dragenter.prevent="handleDragEnter"
          @dragover.prevent=""
          @dragleave="handleDragLeave"
          @drop="dropHighlighted = false"
          @drop.stop.prevent="handleFileDrop"
        >
          <SVGIcon
            v-if="!parsingFiles"
            id="upload-icon"
            class="file-upload-btn__upload-icon"
            :iconName="'cloud_upload'"
            :width="'100%'"
            :height="'200px'"
          />
          <div v-else class="file-upload-btn__parsing-files">
            <div id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
          </div>
          <div id="file-upload-btn-text" class="file-upload-btn__text">
            <span>
              {{ uploadHintPrefix }}
              <span
                v-if="allowsFileSelection"
                class="highlight"
                @click="!parsingFiles ? chooseFiles() : null"
              >select files</span>
              <span v-if="allowsFileSelection && allowsFolderSelection">, or </span>
              <span
                v-if="allowsFolderSelection"
                class="highlight"
                @click="!parsingFiles ? chooseSourceFolder() : null"
              >select parent folder</span>
            </span>
          </div>
          <template v-if="fileObjs && fileObjs.length <= 20">
            <div v-if="fileObjs && fileObjs.length > 0" id="file-upload-grid" class="file-grid scrollbar">
              <div v-for="(file, i) in fileObjs" :key="`file-${i}`" class="file-grid__cell">
                <span>{{ file.name }}</span>
                <IconButton
                  :id="`file-${i}-remove-icon`"
                  class="file-grid__remove-icon"
                  :icon="'cancel_filled'"
                  :width="16"
                  :height="16"
                  @click="removeFile(i)"
                />
              </div>
            </div>
          </template>
          <div v-else-if="fileObjs && fileObjs.length > 20" class="file-upload-btn__text">
            <span>{{ fileObjs.length }} files selected</span>
          </div>
        </div>
      </div>

      <v-checkbox
        v-model="shouldCreateImageGroups"
        density="compact"
        hide-details="true"
        :label="imageGroupsLabel"
      />
    </div>

    <div class="image-importer__group">
      <div class="image-importer__heading">
        <h4>{{ destinationLabel }}</h4>
      </div>
      <SchemaGroup
        ref="schemaGroupRef"
        v-model:params="destinationParams"
        :fields="destinationFields"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { showDirectoryPicker } from 'file-system-access';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { encodeID } from '@/assets/js/utils.js';
import SVGIcon from '@/components/SVGIcon.vue';
import IconButton from '@/components/IconButton.vue';
import SchemaGroup from '@/components/Schema/SchemaGroup.vue';
import { useImporterStore } from '@/stores/useImporterStore.js';
import { appSchema } from './schema.js';

defineProps({
  appDetails: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['completed']);

const $toast = useToast();
const router = useRouter();
const store = useStore();
const importerStore = useImporterStore();
const dataConnect = new DatastoreConnect();

const schemaGroupRef = ref(null);
const parsingFiles = ref(false);
const dropHighlighted = ref(false);
const dragCounter = ref(0);
const fileObjs = ref([]);
const shouldCreateImageGroups = ref(Boolean(appSchema.find((field) => field.name === 'source')?.children?.find((field) => field.type === 'bool' && field.name === 'image_groups')?.default));
const imageGroupsToCreate = ref([]);
const destinationParams = ref({});
const createdDataset = ref(null);

const sourceGroup = computed(() => appSchema.find((field) => field.name === 'source') || {});
const destinationGroup = computed(() => appSchema.find((field) => field.name === 'destination') || {});
const sourceLabel = computed(() => sourceGroup.value?.label || 'Source');
const destinationLabel = computed(() => destinationGroup.value?.label || 'Destination');
const sourceFields = computed(() => sourceGroup.value?.children || []);
const destinationFields = computed(() => destinationGroup.value?.children || []);
const sourceUploadField = computed(() => sourceFields.value.find((field) => field.type === 'upload') || {});
const imageGroupsField = computed(() => sourceFields.value.find((field) => field.type === 'bool' && field.name === 'image_groups') || {});

const uploadAccept = computed(() => {
  const filter = sourceUploadField.value?.files_filter;
  return Array.isArray(filter) && filter.length > 0 ? filter.join(',') : null;
});

const allowedImageExtensions = computed(() => {
  const filter = sourceUploadField.value?.files_filter;
  if (!Array.isArray(filter) || filter.length === 0) {
    return [];
  }

  return filter
    .filter((extension) => String(extension).trim().toLowerCase() !== '.zip')
    .map((extension) => String(extension).trim().replace(/^\./, '').toLowerCase())
    .filter((extension) => extension.length > 0);
});

const allowsZipFiles = computed(() => {
  const filter = sourceUploadField.value?.files_filter;
  if (!Array.isArray(filter) || filter.length === 0) {
    return true;
  }
  return filter.some((extension) => String(extension).trim().toLowerCase() === '.zip');
});

const selectionMode = computed(() => {
  const mode = String(sourceUploadField.value?.selection_mode || 'mixed').toLowerCase();
  return ['mixed', 'files', 'folder'].includes(mode) ? mode : 'mixed';
});

const allowsFileSelection = computed(() => selectionMode.value === 'mixed' || selectionMode.value === 'files');
const allowsFolderSelection = computed(() => selectionMode.value === 'mixed' || selectionMode.value === 'folder');
const uploadHintPrefix = computed(() => (selectionMode.value === 'mixed' ? 'Drag and drop, ' : 'Drag and drop or '));
const imageGroupsLabel = computed(() => imageGroupsField.value?.label || 'Create image groups from folder names');

function getFileExtension(fileName) {
  return String(fileName || '').split('.').pop()?.toLowerCase() || '';
}

function isZipFile(fileName) {
  return allowsZipFiles.value && getFileExtension(fileName) === 'zip';
}

function isAllowedImageFile(fileName) {
  if (allowedImageExtensions.value.length === 0) {
    return true;
  }
  return allowedImageExtensions.value.includes(getFileExtension(fileName));
}

function isAllowedFile(fileName) {
  return isAllowedImageFile(fileName) || isZipFile(fileName);
}

function buildRelativePath(rootPath, fileName) {
  return rootPath ? `${rootPath}/${fileName}` : fileName;
}

function setRelativePath(file, relativePath) {
  file.relativePath = relativePath;
  return file;
}

function getZipRootPath(relativePath) {
  return String(relativePath || '').replace(/\.zip$/i, '');
}

function getMimeType(fileName) {
  const extension = getFileExtension(fileName);
  switch (extension) {
  case 'jpg':
  case 'jpeg':
    return 'image/jpeg';
  case 'png':
    return 'image/png';
  case 'bmp':
    return 'image/bmp';
  case 'webp':
    return 'image/webp';
  case 'tif':
  case 'tiff':
    return 'image/tiff';
  default:
    return '';
  }
}

async function extractZipImages(file, relativePath) {
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(file);
  const zipRootPath = getZipRootPath(relativePath);
  const extractedFiles = [];
  const topLevelDirectories = new Set();

  await Promise.all(Object.values(zip.files).map(async (entry) => {
    if (entry.dir) {
      return;
    }

    const normalizedEntryName = entry.name.replace(/^\/+/, '');
    if (!normalizedEntryName || !isAllowedImageFile(normalizedEntryName)) {
      return;
    }

    const pathParts = normalizedEntryName.split('/').filter(Boolean);
    if (pathParts.length > 1) {
      topLevelDirectories.add(pathParts[0]);
    }

    const blob = await entry.async('blob');
    const imageFile = new File([blob], pathParts[pathParts.length - 1], {
      type: getMimeType(normalizedEntryName),
      lastModified: file.lastModified,
    });
    extractedFiles.push(setRelativePath(imageFile, buildRelativePath(zipRootPath, normalizedEntryName)));
  }));

  if (topLevelDirectories.size > 0) {
    imageGroupsToCreate.value.push(...topLevelDirectories);
  }

  return extractedFiles;
}

async function expandSelectedFile(file, relativePath = file.relativePath || file.webkitRelativePath || '') {
  if (isZipFile(file.name)) {
    return extractZipImages(file, relativePath || file.name);
  }

  if (!isAllowedImageFile(file.name)) {
    return [];
  }

  if (!relativePath) {
    return [file];
  }

  return [setRelativePath(file, relativePath)];
}

async function normalizeSelectedFiles(files) {
  const expandedGroups = await Promise.all(files.map((file) => expandSelectedFile(file)));
  return expandedGroups.flat();
}

function chooseFiles() {
  if (!allowsFileSelection.value) {
    return;
  }
  const input = document.getElementById('image-importer-file-select');
  input?.click();
}

async function handleSelectFileInput(event) {
  parsingFiles.value = true;
  imageGroupsToCreate.value = [];
  try {
    const files = Array.from(event.target.files || []).filter((file) => isAllowedFile(file.name));
    fileObjs.value = await normalizeSelectedFiles(files);
  } finally {
    parsingFiles.value = false;
  }
}

function removeFile(index) {
  fileObjs.value.splice(index, 1);
}

function handleDragEnter() {
  dragCounter.value += 1;
  dropHighlighted.value = true;
}

function handleDragLeave() {
  dragCounter.value -= 1;
  if (dragCounter.value <= 0) {
    dropHighlighted.value = false;
    dragCounter.value = 0;
  }
}

async function handleDirectory(dirhandle, relativePath) {
  const promises = [];

  for await (const [name, handle] of dirhandle) {
    if (handle.kind === 'directory') {
      if (dirhandle.name === relativePath) {
        imageGroupsToCreate.value.push(name);
      }
      const newRelativePath = relativePath ? `${relativePath}/${name}` : name;
      promises.push(handleDirectory(handle, newRelativePath));
    } else if (isAllowedFile(name)) {
      promises.push(
        handle.getFile().then(async (file) => {
          const expandedFiles = await expandSelectedFile(file, `${relativePath}/${name}`);
          fileObjs.value.push(...expandedFiles);
        }),
      );
    }
  }

  await Promise.all(promises);
}

async function chooseSourceFolder() {
  if (!allowsFolderSelection.value) {
    return;
  }
  try {
    const directoryHandle = await showDirectoryPicker({ _preferPolyfill: false });
    parsingFiles.value = true;
    fileObjs.value = [];
    imageGroupsToCreate.value = [];
    await handleDirectory(directoryHandle, directoryHandle.name);
  } catch (error) {
    if (!String(error).includes('AbortError')) {
      $toast.open({
        queue: true,
        duration: 0,
        message: String(error),
        type: 'error',
        dismissible: true,
        position: 'top',
      });
    }
  } finally {
    parsingFiles.value = false;
  }
}

async function collectDroppedDirectoryFiles(directoryHandle, relativePath = directoryHandle.name) {
  const files = [];

  for await (const [name, handle] of directoryHandle) {
    if (handle.kind === 'directory') {
      files.push(...await collectDroppedDirectoryFiles(handle, `${relativePath}/${name}`));
    } else {
      const file = await handle.getFile();
      if (isAllowedFile(file.name)) {
        files.push(...await expandSelectedFile(file, `${relativePath}/${name}`));
      }
    }
  }

  return files;
}

async function collectDroppedEntry(entry) {
  if (!entry) {
    return { files: [], imageGroups: [] };
  }

  if (entry.kind === 'directory') {
    if (!allowsFolderSelection.value) {
      return { files: [], imageGroups: [] };
    }

    const [imageGroups, files] = await Promise.all([
      listTopLevelDirectories(entry),
      collectDroppedDirectoryFiles(entry),
    ]);

    return { files, imageGroups };
  }

  if (!allowsFileSelection.value) {
    return { files: [], imageGroups: [] };
  }

  const file = await entry.getFile();
  return {
    files: await expandSelectedFile(file),
    imageGroups: [],
  };
}

async function listTopLevelDirectories(directoryHandle) {
  const directoryNames = [];

  for await (const [name, handle] of directoryHandle) {
    if (handle.kind === 'directory') {
      directoryNames.push(name);
    }
  }

  return directoryNames;
}

async function handleFileDrop(event) {
  dragCounter.value = 0;
  dropHighlighted.value = false;
  parsingFiles.value = true;
  imageGroupsToCreate.value = [];

  try {
    const items = Array.from(event.dataTransfer?.items || []);
    if (items.length > 0 && typeof items[0].getAsFileSystemHandle === 'function') {
      const entries = await Promise.all(
        items
          .filter((item) => item.kind === 'file')
          .map((item) => item.getAsFileSystemHandle()),
      );

      const droppedEntries = await Promise.all(entries.map((entry) => collectDroppedEntry(entry)));
      imageGroupsToCreate.value.push(...droppedEntries.flatMap((entry) => entry.imageGroups));
      fileObjs.value = droppedEntries.flatMap((entry) => entry.files);
      return;
    }

    if (!allowsFileSelection.value) {
      fileObjs.value = [];
      return;
    }

    const files = Array.from(event.dataTransfer?.files || []).filter((file) => isAllowedFile(file.name));
    fileObjs.value = await normalizeSelectedFiles(files);
  } finally {
    parsingFiles.value = false;
  }
}

function isCreatingNewDataset() {
  return Object.prototype.hasOwnProperty.call(destinationParams.value || {}, 'new_dataset');
}

function getExistingDatasetInfo() {
  return destinationParams.value?.new_dataset__on_false?.dataset_info || null;
}

function getNewDatasetInfo() {
  return destinationParams.value?.new_dataset || {};
}

function buildImportParams() {
  const datasetInfo = getExistingDatasetInfo();
  const newDataset = getNewDatasetInfo();
  const datasetId = Number(datasetInfo?.dataset_id || createdDataset.value?.id || 0);
  const dataset = store.state?.datasets?.datasetList?.find((entry) => entry.id === datasetId) || createdDataset.value;

  return {
    import_type: 'flat',
    dataset_id: datasetId,
    dataset_name: dataset?.name || '',
    project_id: Number(datasetInfo?.project_id || createdDataset.value?.project_id || newDataset?.project_id || store.state.projects.currentProject?.id || 0),
    annotation_set_id: datasetInfo?.annotation_set_id || createdDataset.value?.annotation_sets?.[0]?.id || null,
    tag_name: datasetInfo?.tag_name || '',
    train_group_name: datasetInfo?.train_group_name || '',
    val_group_name: datasetInfo?.val_group_name || '',
    create_image_groups: shouldCreateImageGroups.value ? imageGroupsToCreate.value : undefined,
  };
}

function getDatasetRoute(projectId, datasetId) {
  return router.resolve({
    name: 'datasets.manager',
    params: {
      projectId: encodeID(projectId, 'p'),
    },
    hash: `#dataset-${datasetId}`,
  });
}

function buildDatasetToastMessage(datasetId) {
  const datasetUid = encodeID(datasetId, 'ds');
  return `Importing Images to ${datasetUid}`;
}

function navigateToDatasetCard(projectId, datasetId) {
  if (!projectId || !datasetId) {
    return;
  }

  const route = getDatasetRoute(projectId, datasetId);
  router.push(route);
}

async function createDatasetForImport() {
  const newDataset = getNewDatasetInfo();
  const response = await dataConnect.createDataset({
    name: newDataset.name,
    description: newDataset.description,
    annotation_set_name: newDataset.annotation_set_name || 'annotations',
    project_id: newDataset.project_id,
  });

  if (response?.error || !response?.result) {
    throw new Error(response?.error?.message || response?.error || 'Failed to create dataset');
  }

  createdDataset.value = response.result;

  return response.result;
}

async function s3MultipleFileUpload({ fileList, importParams, ignoreDuplicate = false }) {
  const keys = [];
  for (const file of fileList) {
    const relativePath = file.relativePath || file.webkitRelativePath;
    const trimmedPath = relativePath ? relativePath.split('/').slice(1) : [file.name];
    keys.push(trimmedPath.join('/'));
  }

  const presignedResponse = await dataConnect.s3CreatePresignedUrl({
    dataset_id: importParams.dataset_id,
    ignore_duplicate: ignoreDuplicate,
    keys,
  });

  if (presignedResponse?.error !== undefined) {
    throw presignedResponse.error;
  }

  const presignedResult = presignedResponse?.result || {};

  if (presignedResult.duplicate_count > 0 && !ignoreDuplicate) {
    throw new Error(`Destination dataset contains ${presignedResult.duplicate_count} duplicate file${presignedResult.duplicate_count === 1 ? '' : 's'}.`);
  }

  const newFileList = [];
  if (presignedResult) {
    const {
      importerSource, indexes = [], presignedUrls = [], tnPresignedUrls = [],
    } = presignedResult;

    if (importerSource) {
      importParams.importer_source = importerSource;
    }

    presignedUrls.forEach((url, index) => {
      newFileList.push({
        url,
        thumbnail_url: tnPresignedUrls[index],
        file: fileList[indexes[index]],
      });
    });
  }

  return importerStore.importByType({
    urls: newFileList,
    importParams,
  });
}

function validateRequiredFields() {
  const missingFields = [];
  if (!fileObjs.value.length) {
    missingFields.push(sourceUploadField.value?.label || 'Upload Images');
  }
  if (schemaGroupRef.value) {
    missingFields.push(...schemaGroupRef.value.validateRequiredFields());
  }
  return missingFields;
}

async function start() {
  const missingFields = validateRequiredFields();
  if (missingFields.length > 0) {
    const uniqueMissingFields = Array.from(new Set(missingFields));
    throw new Error(`Required field${uniqueMissingFields.length > 1 ? 's' : ''} missing: ${uniqueMissingFields.join(', ')}`);
  }

  if (isCreatingNewDataset()) {
    await createDatasetForImport();
  }

  const importParams = buildImportParams();
  if (!importParams.dataset_id) {
    throw new Error('A destination dataset is required.');
  }

  const importPromise = s3MultipleFileUpload({
    fileList: fileObjs.value,
    importParams,
  });

  $toast.open({
    queue: true,
    duration: 8000,
    dismissible: true,
    message: buildDatasetToastMessage(importParams.dataset_id),
    type: 'success',
    position: 'top',
  });
  emit('completed');
  navigateToDatasetCard(importParams.project_id, importParams.dataset_id);

  importPromise.catch((error) => {
    const errorMessage = error?.message || String(error || 'Image import failed');
    $toast.open({
      queue: true,
      duration: 0,
      message: errorMessage,
      type: 'error',
      dismissible: true,
      position: 'top',
    });
  });
}

defineExpose({
  start,
  validateRequiredFields,
});
</script>

<style lang="scss" scoped>
.image-importer {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.image-importer__heading {
  display: flex;
  align-items: center;
}

.image-importer__heading h4 {
  width: fit-content;
  margin-bottom: 0;
  font-size: 15px;
  white-space: nowrap;
  text-align: left;
  color: #250E81;
}

.field-group {
  margin-bottom: 16px;
}

.file-upload-btn {
  min-height: 180px;
  border: 2px dashed #b4b4b4;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.file-upload-btn._highlight {
  border-color: #250E81;
  background: rgba(37, 14, 129, 0.08);
}

.file-upload-btn__upload-icon {
  max-width: 70px;
  max-height: 70px;
}

.file-upload-btn__text {
  text-align: center;
  font-size: 14px;
  font-weight: 600;
}

.highlight {
  color: #250E81;
  cursor: pointer;
}

.highlight:hover {
  text-decoration: underline;
}

.file-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  max-height: 180px;
  width: 100%;
  margin-top: 12px;
}

.file-grid__cell {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.04);
}

.file-grid__cell span {
  font-size: 13px;
  font-weight: 500;
  overflow-wrap: anywhere;
}

.d-none {
  display: none;
}
</style>
